import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { addSpecialCase, getSpecialCases } from "../../../components/collabs/collabsSlice";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import Langs, { Localize } from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": {
    fontSize: "16px",
    transition: TRANSITION_SMOOTH,
  },
  "& textarea": {
    fontSize: "16px",
  },
  "& input::placeholder, & textarea::placeholder": {
    opacity: 0.45,
  },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MobileAddSpecialCaseModal = ({ visible, onHide }) => {
  const dispatch = useDispatch();
  const allCollabs = useSelector((state) => state.collabs.allCollabsList);
  const specialCasesOptions = useSelector((state) => state.collabs.specialCasesOptions);

  const [specialCaseData, setSpecialCaseData] = useState({});
  const [errors, setErrors] = useState({});

  const collabOptions = allCollabs.map((collab) => ({ label: collab.label, value: collab.id }));

  const validate = () => {
    const newErrors = {};
    if (!specialCaseData.id_collab) newErrors.id_collab = "Collaborateur est obligatoire";
    if (!specialCaseData.case) newErrors.case = "Cas est obligatoire";
    if (!specialCaseData.start_date) newErrors.start_date = "Date de début est obligatoire";
    if (!specialCaseData.end_date) newErrors.end_date = "Date de fin est obligatoire";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDateForDB = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const formattedData = {
        ...specialCaseData,
        start_date: formatDateForDB(specialCaseData.start_date),
        end_date: formatDateForDB(specialCaseData.end_date),
      };
      const response = await dispatch(
        addSpecialCase({
          special_case: formattedData,
        })
      );

      if (response.payload.responseData.status === 1) {
        showSuccessToast("Cas spécial ajouté avec succès !");
        dispatch(getSpecialCases());
        onHide();
        setSpecialCaseData({});
        setErrors({});
      } else {
        showErrorToast(response.payload.error || "Échec de l'ajout du cas spécial.");
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout du cas spécial :", error);
      showErrorToast("Erreur interne du serveur.");
    }
  };

  const handleChange = (field, value) => {
    setSpecialCaseData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleClose = () => {
    onHide();
    setSpecialCaseData({});
    setErrors({});
  };

  const selectedCollab = collabOptions.find((o) => o.value === specialCaseData.id_collab) || null;
  const selectedCase = specialCasesOptions.find((o) => o.value === specialCaseData.case) || null;

  return (
    <Dialog
      fullScreen
      open={!!visible}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "var(--surface-ground, #f4f6f8)",
        },
      }}
    >
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{
              transition: TRANSITION_SMOOTH,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.06)",
                transform: "scale(1.08)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              ml: 1.5,
              flex: 1,
              fontWeight: 600,
              fontSize: "1.15rem",
              letterSpacing: "-0.02em",
            }}
            variant="h6"
          >
            {Localize("ADD_SPECIAL_CASE")}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          p: 2.5,
          pb: 14,
          overflowY: "auto",
          flex: 1,
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
        }}
      >
        <Box
          sx={{
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.45)",
            boxShadow: "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
            p: 2.5,
            transition: TRANSITION_SMOOTH,
          }}
        >
          {/* Collaborateur */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="Collaborateur" /> *
          </Typography>
          <Autocomplete
            options={collabOptions}
            getOptionLabel={(option) => option.label || ""}
            value={selectedCollab}
            onChange={(_, newValue) => handleChange("id_collab", newValue ? newValue.value : null)}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionnez un collaborateur"
                error={!!errors.id_collab}
                helperText={errors.id_collab || ""}
                sx={glassmorphicTextFieldSx}
              />
            )}
            slotProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
          />

          {/* Cas */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="Cas" /> *
          </Typography>
          <Autocomplete
            options={specialCasesOptions}
            getOptionLabel={(option) => option.label || ""}
            value={selectedCase}
            onChange={(_, newValue) => handleChange("case", newValue ? newValue.value : null)}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionnez un cas"
                error={!!errors.case}
                helperText={errors.case || ""}
                sx={glassmorphicTextFieldSx}
              />
            )}
            slotProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
          />

          {/* Date de début */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="Date de début" /> *
          </Typography>
          <TextField
            type="date"
            fullWidth
            value={specialCaseData.start_date || ""}
            onChange={(e) => handleChange("start_date", e.target.value)}
            error={!!errors.start_date}
            helperText={errors.start_date || ""}
            sx={glassmorphicTextFieldSx}
          />

          {/* Date de fin */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="Date de fin" /> *
          </Typography>
          <TextField
            type="date"
            fullWidth
            value={specialCaseData.end_date || ""}
            onChange={(e) => handleChange("end_date", e.target.value)}
            error={!!errors.end_date}
            helperText={errors.end_date || ""}
            inputProps={{
              min: specialCaseData.start_date || undefined,
            }}
            sx={glassmorphicTextFieldSx}
          />

          {/* Comments */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="Comments" />
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={specialCaseData.comments || ""}
            onChange={(e) => handleChange("comments", e.target.value)}
            sx={glassmorphicTextFieldSx}
          />
        </Box>
      </Box>

      {/* Sticky bottom buttons */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2.5,
          bgcolor: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
          borderTop: "1px solid rgba(255,255,255,0.5)",
          zIndex: 1,
          display: "flex",
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          fullWidth
          onClick={handleClose}
          sx={{
            minHeight: 52,
            borderRadius: BORDER_RADIUS_BUTTON,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            transition: TRANSITION_SMOOTH,
            "&:hover": { transform: "scale(1.02)" },
            "&:active": { transform: "scale(0.98)" },
          }}
        >
          {Localize("Cancel")}
        </Button>
        <Button
          variant="contained"
          color="warning"
          fullWidth
          onClick={handleSubmit}
          sx={{
            minHeight: 52,
            borderRadius: BORDER_RADIUS_BUTTON,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
          }}
        >
          {Localize("ADD")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default MobileAddSpecialCaseModal;
